import { useTranslation } from 'react-i18next';
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList
} from '@/components/ui/combobox';
import { Spinner } from '@/components/ui/spinner';
import { useSimpleSelect } from './model';

export interface SelectOption {
  value: string;
  label: string;
  /** Extra strings the option also matches when filtering (e.g. organization abbreviation). */
  keywords?: string[];
}

interface SimpleSelectProps {
  options: SelectOption[];
  value?: string;
  onChange: (value: string | undefined) => void;
  onInputValueChange?: (value: string) => void;
  placeholder?: string;
  /**
   * Query the field starts with, e.g. the organization name an SRF import could not
   * resolve. It seeds the search so the user sees candidate matches straight away;
   * the field itself stays unselected until they pick one.
   */
  initialQuery?: string;
  emptyMessage?: string;
  className?: string;
  isLoading?: boolean;
  disabled?: boolean;
}

export function SimpleSelect({
  options,
  value,
  onChange,
  onInputValueChange,
  placeholder = 'Select an option',
  initialQuery,
  emptyMessage = 'No items found.',
  className = 'w-60',
  isLoading = false,
  disabled = false
}: SimpleSelectProps) {
  const { t } = useTranslation();
  const { items, selectedLabel, handleValueChange, getKeywords } = useSimpleSelect({
    options,
    value,
    onChange
  });

  const comboboxFilter = ComboboxPrimitive.useFilter();

  const isSelectingRef = useRef(false);
  const [inputValue, setInputValue] = useState(selectedLabel || initialQuery || '');
  const isSeedPendingRef = useRef(Boolean(initialQuery) && !selectedLabel);
  const isSeedUsedRef = useRef(Boolean(initialQuery));

  // A seeded query arrives as the displayed value rather than as typing. It stops
  // applying the moment the field is edited or a real option is chosen.
  const seededQuery = initialQuery && !selectedLabel && inputValue === initialQuery ? initialQuery : '';

  // Same collator matching as the Combobox default, extended to option keywords
  // (e.g. organization abbreviation) so server results matched by them survive.
  const filter = useMemo(
    () => (label: string, query: string) => {
      const trimmedQuery = query.trim();
      if (!trimmedQuery || trimmedQuery === selectedLabel) return true;
      if (comboboxFilter.contains(label, trimmedQuery)) return true;
      return getKeywords(label)?.some((keyword) => comboboxFilter.contains(keyword, trimmedQuery)) ?? false;
    },
    [comboboxFilter, selectedLabel, getKeywords]
  );

  // Base UI narrows the list by what the user typed, and a seeded query is displayed
  // rather than typed — so the narrowing for it happens here instead.
  const visibleItems = useMemo(() => {
    if (!seededQuery) return items;
    return items.filter(
      (label) =>
        comboboxFilter.contains(label, seededQuery) ||
        (getKeywords(label)?.some((keyword) => comboboxFilter.contains(keyword, seededQuery)) ?? false)
    );
  }, [items, seededQuery, comboboxFilter, getKeywords]);

  const handleInputChange = useCallback(
    (newValue: string) => {
      setInputValue(newValue);

      if (!isSelectingRef.current && onInputValueChange) {
        onInputValueChange(newValue);
      }
    },
    [onInputValueChange]
  );

  const handleSelection = useCallback(
    (label: string | null) => {
      isSelectingRef.current = true;
      handleValueChange(label);
      setInputValue(label || '');

      setTimeout(() => {
        isSelectingRef.current = false;
      }, 100);
    },
    [handleValueChange]
  );

  useEffect(() => {
    if (isSelectingRef.current) return;

    // The seeded query has to survive this sync on mount, where there is nothing
    // selected yet. It applies exactly once — a later clear must not bring it back.
    if (isSeedPendingRef.current) {
      isSeedPendingRef.current = false;
      if (!selectedLabel) return;
    }

    setInputValue(selectedLabel || '');
  }, [selectedLabel]);

  useEffect(() => {
    if (!initialQuery || isSeedUsedRef.current) return;
    isSeedUsedRef.current = true;
    if (!selectedLabel) setInputValue((current) => current || initialQuery);
  }, [initialQuery, selectedLabel]);

  return (
    <Combobox
      items={visibleItems}
      value={inputValue}
      onValueChange={handleSelection}
      onInputValueChange={handleInputChange}
      filter={filter}
      disabled={disabled}>
      <ComboboxInput
        placeholder={placeholder}
        className={className}
        showClear={!!inputValue && !disabled}
        showTrigger={!disabled}
        disabled={disabled}
      />
      <ComboboxContent>
        {isLoading ? (
          <ComboboxEmpty className="flex items-center justify-center gap-2 py-6">
            <Spinner className="size-3.5" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {t('common.loading')}
            </span>
          </ComboboxEmpty>
        ) : (
          <>
            <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
            <ComboboxList>
              {(label) => (
                <ComboboxItem key={label} value={label}>
                  {label}
                </ComboboxItem>
              )}
            </ComboboxList>
          </>
        )}
      </ComboboxContent>
    </Combobox>
  );
}
